import { applyEasing } from '../easing.js'

// Blends two already-rendered frames (offscreen canvases) for the clip-boundary transition window.
// `progress` goes 0 (fully "from") -> 1 (fully "to"). `direction` applies to wipe/slide (default
// 'left'-to-right movement/reveal) - 'right'/'up'/'down' just flip which axis/sign is used.
export const blendTransition = (ctx, { fromCanvas, toCanvas, canvasSize, type, progress, direction = 'left' }) => {
    const { width, height } = canvasSize
    const p = Math.min(Math.max(progress, 0), 1)
    const isVertical = direction === 'up' || direction === 'down'
    const sign = direction === 'right' || direction === 'down' ? -1 : 1

    ctx.clearRect(0, 0, width, height)

    switch (type) {
        case 'wipe': {
            ctx.drawImage(fromCanvas, 0, 0)
            ctx.save()
            ctx.beginPath()
            if (isVertical) {
                const wipeHeight = height * p
                const y = direction === 'down' ? height - wipeHeight : 0
                ctx.rect(0, y, width, wipeHeight)
            } else {
                const wipeWidth = width * p
                const x = direction === 'right' ? width - wipeWidth : 0
                ctx.rect(x, 0, wipeWidth, height)
            }
            ctx.clip()
            ctx.drawImage(toCanvas, 0, 0)
            ctx.restore()
            break
        }
        case 'slide': {
            if (isVertical) {
                ctx.drawImage(fromCanvas, 0, -sign * height * p)
                ctx.drawImage(toCanvas, 0, sign * height * (1 - p))
            } else {
                ctx.drawImage(fromCanvas, -sign * width * p, 0)
                ctx.drawImage(toCanvas, sign * width * (1 - p), 0)
            }
            break
        }
        case 'zoom': {
            // Crossfade while the incoming clip scales in from slightly smaller than full size -
            // a bit more energy than a plain fade without needing a hard directional cut.
            ctx.globalAlpha = 1
            ctx.drawImage(fromCanvas, 0, 0)
            ctx.globalAlpha = p
            const scale = 0.85 + 0.15 * p
            const dw = width * scale
            const dh = height * scale
            ctx.drawImage(toCanvas, (width - dw) / 2, (height - dh) / 2, dw, dh)
            ctx.globalAlpha = 1
            break
        }
        case 'blur': {
            // Crossfade with a blur that peaks in the middle of the transition, softening the cut
            // between two visually very different clips instead of a sharp dissolve.
            const blurPx = Math.sin(p * Math.PI) * 18
            ctx.filter = blurPx > 0.5 ? `blur(${blurPx}px)` : 'none'
            ctx.globalAlpha = 1
            ctx.drawImage(fromCanvas, 0, 0)
            ctx.globalAlpha = p
            ctx.drawImage(toCanvas, 0, 0)
            ctx.globalAlpha = 1
            ctx.filter = 'none'
            break
        }
        case 'fade':
        default: {
            ctx.globalAlpha = 1
            ctx.drawImage(fromCanvas, 0, 0)
            ctx.globalAlpha = p
            ctx.drawImage(toCanvas, 0, 0)
            ctx.globalAlpha = 1
            break
        }
    }
}

// Given the media track's clips (already laid out with timelineStartMs/timelineEndMs) and the
// current global time, returns null if no transition is in progress, or the transition context
// { fromClip, toClip, type, progress, windowStartMs } describing the crossfade in progress.
export const getTransitionAt = (clips, globalMs) => {
    for (let i = 0; i < clips.length - 1; i++) {
        const current = clips[i]
        const next = clips[i + 1]
        const durationMs = (current.transitionOut && current.transitionOut.durationMs) || 0
        const type = (current.transitionOut && current.transitionOut.type) || 'none'
        if (type === 'none' || durationMs <= 0) continue

        // Centered on the cut point (current.timelineEndMs, which is also next.timelineStartMs -
        // clips always sit back-to-back, see recomputeTimelineLayout) rather than fully consumed
        // *before* it - half the duration plays out before the cut and half after, matching where
        // the user actually placed the transition instead of always finishing exactly at the cut.
        const boundaryMs = current.timelineEndMs
        const halfDurationMs = durationMs / 2
        const windowStartMs = boundaryMs - halfDurationMs
        const windowEndMs = boundaryMs + halfDurationMs
        if (globalMs >= windowStartMs && globalMs < windowEndMs) {
            const rawProgress = (globalMs - windowStartMs) / durationMs
            return {
                fromClip: current,
                toClip: next,
                type,
                direction: (current.transitionOut && current.transitionOut.direction) || 'left',
                progress: applyEasing(rawProgress, current.transitionOut && current.transitionOut.easingPoints),
                windowStartMs
            }
        }
    }
    return null
}
